import React, { useState, useEffect, useCallback } from "react";
import cartService from "./services/cart.service";
import favouriteService from "./services/favourite.service";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  loginSuccess,
  logoutUser,
  setLoading as setAuthLoading,
} from "./store/authSlice";

// Firebase
import { auth } from "./config/firebase";
import { onAuthStateChanged } from "firebase/auth";

// React Router
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import "./App.css";

// Layout
import Header from "./components/layout/Header/Header";
import Footer from "./components/layout/Footer/Footer";

// Common
import PageLoader from "./components/common/LoadingSpinner/LoadingSpinner";

// Pages
import AuthPage from "./pages/Auth/AuthPage";
import CartPage from "./pages/Cart/CartPage";
import ContactPage from "./pages/Contact/ContactPage";
import ShopPage from "./pages/Shop/ShopPage";
import ProductDetailPage from "./pages/Product/ProductDetailPage";
import HeroSection from "./pages/Home/HeroSection";
import PopularCategories from "./pages/Home/PopularCategories";
import FeaturedProducts from "./pages/Home/FeaturedProducts";
import ProductSlider from "./pages/Home/ProductSlider";
import RecentlyLaunched from "./pages/Home/Recentlylaunched";
// import ContactSection from "./pages/Home/ContactSection";
import AccountPage from "./pages/Account/AccountPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import BatteryEstimator from "./pages/Home/BatteryEstimator";
import BatteryEducation from "./pages/Home/BatteryEducation";
import PrivacyPolicy from "./pages/Policy/PrivacyPolicy";
import RefundCancellation from "./pages/Policy/RefundCancellation";
import AboutUs from "./pages/Policy/AboutUs";
import TermsConditions from "./pages/Policy/TermsConditions";
import FavouritePage from "./pages/Favourite/FavouritePage";
import IntegratedInverter from "./pages/Home/IntegratedInverter";
import PowerEvolution from "./pages/Home/PowerEvolution";
import ZynvertStandard from "./pages/Home/ZynvertStandard";
import AnnouncementBanner from "./components/layout/Header/AnnounementBanner";

// Admin
import AdminLayout from "./pages/Admin/AdminLayout";
import OrderDetailsPage from "./pages/Account/OrderDetailsPage";

// ✅ IMPORT THE WARRANTY CHECK PAGE
import WarrantyCheck from "./pages/Warranty/WarrantyCheck";

// ======== MOCK DATA ========
const footerCategories = [
  /* ... */
];

const footerPolicies = [
  /* ... */
];

const INITIAL_CART_MOCK = [
  {
    id: "battery",
    name: "Zynvert 12V 100Ah LiFePO4 Battery",
    price: 15999,
    quantity: 1,
    image: "https://placehold.co/150x150/eeeeee/333333?text=Zynvert+100Ah",
  },
  {
    id: "inverter",
    name: "Solar Integrated Inverter 3kW",
    price: 45500,
    quantity: 1,
    image: "https://placehold.co/150x150/eeeeee/333333?text=Solar+Inverter",
  },
];

// ======== HELPERS ========
const toUrlFriendly = (text) => {
  if (!text) return "";

  let str;
  if (typeof text === "string") {
    str = text;
  } else if (typeof text?.name === "string") {
    str = text.name;
  } else {
    str = String(text);
  }

  return str
    .toLowerCase()
    .replace(/ & /g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

// ======== HOME PAGE COMPOSITION ========
const HomePage = ({
  onNavigate,
  onAddToCart,
  cartItems,
  favouriteItems,
  onQuantityChange,
  onRemoveItem,
}) => (
  <>
    <HeroSection />
    <PopularCategories
      onAddToCart={onAddToCart}
      cartItems={cartItems}
      favouriteItems={favouriteItems}
      onQuantityChange={onQuantityChange}
      onRemoveItem={onRemoveItem}
    />
    <ProductSlider
      title="⚡ Hot Deals & Trending Items"
      onNavigate={onNavigate}
      onAddToCart={onAddToCart}
      cartItems={cartItems}
      favouriteItems={favouriteItems}
      onQuantityChange={onQuantityChange}
      onRemoveItem={onRemoveItem}
    />
    <FeaturedProducts
      onNavigate={onNavigate}
      onAddToCart={onAddToCart}
      cartItems={cartItems}
      favouriteItems={favouriteItems}
      onQuantityChange={onQuantityChange}
      onRemoveItem={onRemoveItem}
    />
    <RecentlyLaunched
      onNavigate={onNavigate}
      onAddToCart={onAddToCart}
      cartItems={cartItems}
      favouriteItems={favouriteItems}
      onQuantityChange={onQuantityChange}
      onRemoveItem={onRemoveItem}
    />
    <IntegratedInverter />
    <PowerEvolution />
    <BatteryEducation />
    <BatteryEstimator />
    <ZynvertStandard />
  </>
);

// ======== PRODUCT DETAIL ROUTE WRAPPER ========
const ProductDetailRoute = ({
  onNavigate,
  onAddToCart,
  cartItems,
  favouriteItems,
  onQuantityChange,
  onRemoveItem,
}) => {
  const { productId } = useParams();
  return (
    <ProductDetailPage
      productId={productId || "battery"}
      onNavigate={onNavigate}
      onAddToCart={onAddToCart}
      cartItems={cartItems}
      favouriteItems={favouriteItems}
      onQuantityChange={onQuantityChange}
      onRemoveItem={onRemoveItem}
    />
  );
};

// ======== MAIN SHELL ========
const AppShell = () => {
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    loading: authLoading,
    user,
  } = useSelector((state) => state.auth);

  const [dataLoaded, setDataLoaded] = useState(false);

 const [cartItems, setCartItems] = useState(INITIAL_CART_MOCK);
  const [favouriteItems, setFavouriteItems] = useState([]);

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const navigate = useNavigate();
  const location = useLocation();

  // 1. Set the initial page based on the actual URL, not a hardcoded "home"
  const [currentPage, setCurrentPage] = useState(() => {
    const path = location.pathname;
    if (path === "/") return "home";
    return path.split("/")[1] || "home"; 
  });

  // 2. Keep the Header synced if the user clicks the browser's Back/Forward arrows
  useEffect(() => {
    const path = location.pathname;
    if (path === "/") {
      setCurrentPage("home");
    } else {
      setCurrentPage(path.split("/")[1]);
    }
  }, [location.pathname]);

  const isAuthPage = location.pathname === "/auth";
  const isAdminRoute = location.pathname.startsWith("/admin");

  // ===== Cart handlers =====
  const handleAddToCart = async (productToAdd) => {
    const productId = productToAdd.product_id;

    if (!productId || typeof productId !== "string") {
      console.error(
        "Invalid product: missing inventory product_id",
        productToAdd,
      );
      return;
    }

    const image = productToAdd.images?.[0] || null;
    const result = await cartService.addToCart(productId, 1, image);

    if (result?.notLoggedIn) {
      window.location.href = "/auth";
      return;
    }

    const cart = await cartService.getCart();

    const formattedItems =
      cart.CartItems?.map((item) => ({
        cartItemId: item.cart_item_id,
        productId: item.product_id,
        name: item.Inventory?.name,
        sku: item.Inventory?.sku,
        price: Number(item.Inventory?.current_price),
        quantity: item.quantity,
        image: item.image || null,
        base_price: item.Inventory?.base_price,
        gst_rate: item.Inventory?.gst_rate,
        hsn_code: item.Inventory?.hsn_code,
      })) || [];

    setCartItems(formattedItems);
  };

  const handleQuantityChange = async (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await cartService.updateCartItem(cartItemId, newQuantity);

      setCartItems((items) =>
        items.map((item) =>
          item.cartItemId === cartItemId
            ? { ...item, quantity: newQuantity }
            : item,
        ),
      );
    } catch (err) {
      console.error("Quantity update failed", err);
    }
  };

  const removeItem = async (cartItemId) => {
    try {
      await cartService.removeCartItem(cartItemId);

      setCartItems((items) =>
        items.filter((item) => item.cartItemId !== cartItemId),
      );
    } catch (err) {
      console.error("Remove item failed", err);
    }
  };

  // ===== Load cart & favourites from backend after login =====
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!isAuthenticated) {
          setCartItems([]);
          setFavouriteItems([]);
          return;
        }

        // 1. Load Cart
        const cart = await cartService.getCart();
        const formattedCartItems =
          cart?.CartItems?.map((item) => ({
            cartItemId: item.cart_item_id,
            productId: item.product_id,
            name: item.Inventory?.name || "Product",
            sku: item.Inventory?.sku || "",
            price: Number(item.Inventory?.current_price),
            quantity: item.quantity,
            image: item.image || null,
            base_price: item.Inventory?.base_price,
            gst_rate: item.Inventory?.gst_rate,
            hsn_code: item.Inventory?.hsn_code,
          })) || [];
        setCartItems(formattedCartItems);

        // 2. Load Favourites
        const favs = await favouriteService.getFavourites();
        if (Array.isArray(favs)) {
          setFavouriteItems(favs);
        }
      } catch (err) {
        console.error("Failed to load data", err);
      }
      window.scrollTo(0, 0);
    };

    loadData();

    // 3. Listen for changes when user clicks a heart in ProductCard or FavouritePage
    const handleFavChange = async () => {
      if (isAuthenticated) {
        try {
          const favs = await favouriteService.getFavourites();
          if (Array.isArray(favs)) setFavouriteItems(favs);
        } catch (err) {
          console.error("Failed to refresh favourites", err);
        }
      }
    };
    window.addEventListener("favouritesChanged", handleFavChange);

    return () =>
      window.removeEventListener("favouritesChanged", handleFavChange);
  }, [isAuthenticated]);

  // ===== Navigation helper =====
  const handleNavigate = useCallback(
    (page, productId = null) => {
      setCurrentPage(page);
      switch (page) {
        case "home":
        case "/":
          navigate("/");
          break;
        case "shop":
          navigate("/shop");
          break;
        case "about":
        case "about-us":
          navigate("/about-us");
          break;
        case "cart":
          navigate("/cart");
          break;
        case "contact":
          navigate("/contact");
          break;
        case "check-warranty": // FIXED: Changed to match Header.js
        case "warranty-check":
          navigate("/check-warranty");
          break;
        case "account":
          navigate("/account");
          break;
        case "auth":
        case "login":
          navigate("/auth");
          break;
        case "admin":
          navigate("/admin");
          break;
        case "product":
          navigate(`/product/${productId || "battery"}`);
          break;
        case "favourite":
          navigate("/favourite");
          break;
        default:
          navigate(typeof page === "string" ? page : "/");
      }
    },
    [navigate],
  );

  // ===== Simulated initial data load =====
  useEffect(() => {
    const loadData = async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setDataLoaded(true);
    };
    loadData();
  }, []);

  // ===== Firebase Auth Listener =====
  useEffect(() => {
    dispatch(setAuthLoading(true));

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        dispatch(loginSuccess(firebaseUser));
      } else {
        dispatch(logoutUser());
      }
      dispatch(setAuthLoading(false));
    });

    return () => unsubscribe();
  }, [dispatch]);

  // ===== Intersection Observer for section reveal =====
  useEffect(() => {
    if (authLoading || !dataLoaded) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    const sections = document.querySelectorAll(".section-reveal");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [authLoading, dataLoaded, location.pathname]);

  // ===== Simple auth guard components =====
  const RequireAuth = ({ children, requireAdmin = false }) => {
    if (authLoading) {
      return <PageLoader />;
    }

    if (!isAuthenticated) {
      return <Navigate to="/auth" replace />;
    }

    if (requireAdmin) {
      const isAdmin =
        user?.role === "admin" ||
        user?.isAdmin === true ||
        user?.email === process.env.REACT_APP_ADMIN_EMAIL;

      if (!isAdmin) {
        return <Navigate to="/" replace />;
      }
    }

    return children;
  };

  if (authLoading || !dataLoaded) {
    return <PageLoader />;
  }

  const appContentClass = "visible";

  return (
    <div
      className="App"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      {!isAdminRoute && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 9999,
          }}
        >
          <AnnouncementBanner />
        </div>
      )}

      <div
        className={`app-content ${appContentClass}`}
        style={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          paddingTop: !isAdminRoute ? "38px" : "0px",
        }}
      >
        {!isAdminRoute && (
          <Header
            onNavigate={handleNavigate}
            currentPage={currentPage}
            toUrlFriendly={toUrlFriendly}
            cartItemCount={cartItemCount}
            favItemCount={favouriteItems.length}
          />
        )}

        <main
          style={{
            flexGrow: 1,
            display: isAuthPage ? "flex" : "block",
            justifyContent: isAuthPage ? "center" : "initial",
            alignItems: isAuthPage ? "center" : "initial",
            padding: isAuthPage ? "20px" : "initial",
          }}
        >
          <Routes>
            <Route
              path="/"
              element={
                <HomePage
                  onNavigate={handleNavigate}
                  onAddToCart={handleAddToCart}
                  cartItems={cartItems}
                  favouriteItems={favouriteItems}
                  onQuantityChange={handleQuantityChange}
                  onRemoveItem={removeItem}
                />
              }
            />
            <Route
              path="/shop"
              element={
                <ShopPage
                  onNavigate={handleNavigate}
                  onAddToCart={handleAddToCart}
                  cartItems={cartItems}
                  favouriteItems={favouriteItems}
                  onQuantityChange={handleQuantityChange}
                  onRemoveItem={removeItem}
                />
              }
            />
            <Route
              path="/product/:productId"
              element={
                <ProductDetailRoute
                  onNavigate={handleNavigate}
                  onAddToCart={handleAddToCart}
                  cartItems={cartItems}
                  favouriteItems={favouriteItems}
                  onQuantityChange={handleQuantityChange}
                  onRemoveItem={removeItem}
                />
              }
            />
            <Route
              path="/cart"
              element={
                <CartPage
                  onNavigate={handleNavigate}
                  cartItems={cartItems}
                  handleQuantityChange={handleQuantityChange}
                  removeItem={removeItem}
                  setCartItems={setCartItems}
                />
              }
            />
            <Route path="/contact" element={<ContactPage />} />

            {/* ✅ ROUTE UPDATED TO MATCH HANDLE_NAVIGATE AND URL */}
            <Route path="/check-warranty" element={<WarrantyCheck />} />

            <Route path="/auth" element={<AuthPage />} />

            <Route
              path="/account"
              element={
                <RequireAuth>
                  <AccountPage onNavigate={handleNavigate} />
                </RequireAuth>
              }
            />

            <Route
              path="/admin/*"
              element={
                <RequireAuth requireAdmin>
                  <AdminLayout />
                </RequireAuth>
              }
            />

            <Route
              path="/orders"
              element={
                <RequireAuth>
                  <AccountPage />
                </RequireAuth>
              }
            />

            <Route
              path="*"
              element={
                <div style={{ padding: "4rem 1rem", textAlign: "center" }}>
                  <h1>404 - Page Not Found</h1>
                  <p>The page you are looking for does not exist.</p>
                </div>
              }
            />

            <Route
              path="/payment-success"
              element={<PaymentSuccess setCartItems={setCartItems} />}
            />

            <Route
              path="/account/orders/:orderId"
              element={
                <RequireAuth>
                  <OrderDetailsPage />
                </RequireAuth>
              }
            />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/refund-cancellations"
              element={<RefundCancellation />}
            />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            
            <Route path="/about-us" element={<AboutUs onNavigate={handleNavigate} />} />
            
            <Route
              path="/favourite"
              element={
                <FavouritePage
                  onNavigate={handleNavigate}
                  onAddToCart={handleAddToCart}
                />
              }
            />
          </Routes>
        </main>

        {!isAdminRoute && (
          <Footer
            footerCategories={footerCategories}
            footerPolicies={footerPolicies}
            toUrlFriendly={toUrlFriendly}
          />
        )}
      </div>
    </div>
  );
};

// ======== TOP-LEVEL APP WITH BROWSERROUTER ========
const App = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
);

export default App;